import { CLUB_COLORS, HELMET_DESIGNS, type Helmet } from '@/social/crewLogic';

/**
 * helmetArt.ts — the RACER CLUB helmets, worn beside every name in a club.
 *
 * M142 (owner round): a helmet is now a PRIMARY + SECONDARY pick from the
 * 24-colour club palette (crewLogic CLUB_COLORS) times seven designs, which
 * replace the M140 fixed colourways. The shell wears the primary (a lighter
 * crown, a darker skirt), the design wears the secondary. Any pair is legal,
 * the same colour twice included, so every design element carries a thin
 * CONTRAST EDGE computed from the two colours' lightness: dark on light
 * pairs, light on dark pairs, strongest when the two colours are close.
 *
 * The lineup (index = the database value; each new design took the slot of
 * the M140 design it replaces, so an old client draws something close):
 *   0 CLASSIC · 1 RACING STRIPES · 2 MATCH BALL · 3 CHECKERED · 4 FIREBALL ·
 *   5 DRAGON · 6 LIGHTNING. No design is called CHAMPION any more.
 *
 * The owner's visor fix: the M140 visor pinched to ~8 units at the nose.
 * The shell front and the visor were redrawn as a modern full-face race
 * helmet: a broad eye-port that stays a strong, even band (~20 units) right
 * to the front edge, a solid chin bar underneath, a pivot plate at the
 * visor's back end, a lift tab and a small rear spoiler lip.
 *
 * Review round: close pairs step AWAY from the shell with the hue kept
 * (deepen(): gold darkens to amber, never olive); the dragon sheds all
 * helmet hardware (no screw, vents, lift tab or glare: a jaw fin, jaw
 * ridges and a lip line instead) and wears tapered, ringed horns blended
 * toward bone so a dark pick still reads against the page; the stripes,
 * bolts, checker band and fireball were re-laid to stay clear of the glass
 * and hardware; CHECKERED off squares are a true flag black or white.
 *
 * Inline SVG (viewBox 0 0 120 100, side view facing RIGHT): crisp from a
 * 28 px roster chip to the picker preview, zero downloads. Every SVG gets its
 * own id prefix, because SVG gradient / clip / pattern ids are
 * document-global and a hidden copy's gradient would stop painting for all.
 */

export const HELMET_DESIGN_NAMES: readonly string[] = [
  'CLASSIC',
  'RACING STRIPES',
  'MATCH BALL',
  'CHECKERED',
  'FIREBALL',
  'DRAGON',
  'LIGHTNING',
];

let seq = 0;

// ---- colour ----------------------------------------------------------------------

type Rgb = [number, number, number];

function rgbOf(hex: string): Rgb {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.replace(/./g, (c) => c + c) : h.slice(0, 6);
  const n = parseInt(full, 16);
  if (!Number.isFinite(n)) return [128, 128, 128];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function hexOf([r, g, b]: Rgb): string {
  const c = (v: number) =>
    Math.round(Math.max(0, Math.min(255, v)))
      .toString(16)
      .padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** a → b by t (0 = a, 1 = b), in sRGB — plenty for shading a flat swatch. */
function mix(a: string, b: string, t: number): string {
  const x = rgbOf(a);
  const y = rgbOf(b);
  return hexOf([x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t]);
}

/** CIE L* (0 black … 100 white) — perceived lightness. */
function lightness(hex: string): number {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = rgbOf(hex);
  const y = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return y > 0.008856 ? 116 * Math.cbrt(y) - 16 : 903.3 * y;
}

/** [hue 0-360, saturation 0-1, lightness 0-1]. */
function hslOf(hex: string): [number, number, number] {
  const [r, g, b] = rgbOf(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  let h: number;
  if (max === r) h = ((g - b) / d + 6) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h * 60, d / (1 - Math.abs(2 * l - 1)), l];
}

function hexOfHsl(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const [r, g, b]: Rgb =
    hp < 1
      ? [c, x, 0]
      : hp < 2
        ? [x, c, 0]
        : hp < 3
          ? [0, c, x]
          : hp < 4
            ? [0, x, c]
            : hp < 5
              ? [x, 0, c]
              : [c, 0, x];
  const m = l - c / 2;
  return hexOf([(r + m) * 255, (g + m) * 255, (b + m) * 255]);
}

/** How colourful a swatch is (0 = grey … 1 = a pure hue). */
function chroma(hex: string): number {
  const [r, g, b] = rgbOf(hex);
  return (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
}

/** True for a colour that burns warm (reds → yellows, and greys / blacks,
 *  whose fire glows amber); false for cool hues (they burn like gas, white-hot). */
function burnsWarm(hex: string): boolean {
  if (chroma(hex) < 0.18) return true;
  const h = hslOf(hex)[0];
  return h < 75 || h > 330;
}

const INK = '#0b0d12';
const PALE = '#ffffff';
/** The least lightness (L*) gap a design keeps from its shell. */
const MIN_TONE_GAP = 20;
/** A lighter step tops out near white, so that side settles for this gap
 *  (the dark contrast edge carries the rest). */
const MIN_TONE_GAP_UP = 13;

/**
 * Darker by t (0-1) with the HUE and SATURATION KEPT. An sRGB mix toward
 * black drains saturation, so a yellow turns khaki / olive; this deepens gold
 * to amber, red to oxblood, blue to midnight. Greys simply go darker.
 */
function deepen(hex: string, t: number): string {
  if (chroma(hex) < 0.12) return mix(hex, INK, t);
  const [h, s, l] = hslOf(hex);
  // yellows and golds lean toward amber as they darken, like real gold leaf
  const hue = h > 28 && h < 64 ? h - 14 * t : h;
  return hexOfHsl(hue, s, l * (1 - t));
}

/**
 * Tone-on-tone: when the two picks (nearly) match, the design steps AWAY
 * from the shell (lighter if it is the lighter pick, darker if it is the
 * darker one; a same-colour pair goes the way with more room) until it sits
 * a clear lightness gap off the shell. So a same-colour helmet still reads as
 * a design, and GOLD / SUNBURST shows a pale sunburst on gold, never mud.
 */
function toneStep(s: string, lp: number): string {
  const ls = lightness(s);
  if (Math.abs(ls - lp) >= MIN_TONE_GAP) return s;
  const run = (up: boolean) => {
    let col = s;
    for (let t = 0.06; t <= (up ? 0.85 : 0.8) + 1e-9; t += 0.06) {
      col = up ? mix(s, PALE, t) : deepen(s, t);
      if (Math.abs(lightness(col) - lp) >= MIN_TONE_GAP) break;
    }
    return { col, gap: Math.abs(lightness(col) - lp) };
  };
  const up = Math.abs(ls - lp) < 0.5 ? lp <= 55 : ls > lp;
  const a = run(up);
  if (a.gap >= MIN_TONE_GAP || (up && a.gap >= MIN_TONE_GAP_UP)) return a.col;
  const b = run(!up);
  return b.gap > a.gap ? b.col : a.col;
}

/** Everything one helmet is painted with, derived from its two picks. */
interface Paint {
  /** shell */
  p: string;
  pLight: string;
  pShade: string;
  pDeep: string;
  /** design */
  s: string;
  sLight: string;
  sShade: string;
  /** the contrast edge around design elements (see the header) */
  edge: string;
  edgeOp: number;
  /** a line that reads ON the shell (panel seams, scale rows) */
  seam: string;
  /** the outline of design parts that stand OFF the shell, against the page
   *  (dragon horns + fins): light round a dark secondary, dark round a light one */
  rim: string;
  /** the shell is dark (BLACK, STEALTH, NAVY…) */
  darkShell: boolean;
  /** the shell is near-white (WHITE, ICE, SUNBURST…) */
  lightShell: boolean;
}

function paletteIndex(v: unknown): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : 0;
  return Math.min(CLUB_COLORS.length - 1, Math.max(0, n));
}

function paintFor(h: Helmet): Paint {
  const p = CLUB_COLORS[paletteIndex(h.primary)].hex;
  const s = CLUB_COLORS[paletteIndex(h.secondary)].hex;
  const lp = lightness(p);
  const ls = lightness(s);
  const close = Math.abs(lp - ls);
  const lightPair = (lp + ls) / 2 > 56;
  const edge = lightPair ? INK : PALE;
  const d = toneStep(s, lp);
  return {
    p,
    pLight: mix(p, PALE, lp > 80 ? 0.55 : 0.32),
    pShade: deepen(p, 0.42),
    pDeep: deepen(p, 0.66),
    s: d,
    sLight: mix(d, PALE, 0.42),
    sShade: deepen(d, 0.45),
    edge,
    edgeOp: Math.round(Math.max(0.2, Math.min(0.9, 0.95 - close / 42)) * 100) / 100,
    seam: lp > 55 ? deepen(p, 0.55) : mix(p, PALE, 0.42),
    rim: lightness(d) < 40 ? mix(d, PALE, 0.5) : deepen(d, 0.55),
    darkShell: lp < 32,
    lightShell: lp > 80,
  };
}

// ---- geometry helpers --------------------------------------------------------------

type Pt = [number, number];
type Cubic = [Pt, Pt, Pt, Pt];

const f1 = (v: number) => Math.round(v * 10) / 10;

function cubicAt(c: Cubic, t: number): Pt {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const d = 3 * u * t * t;
  const e = t * t * t;
  return [
    a * c[0][0] + b * c[1][0] + d * c[2][0] + e * c[3][0],
    a * c[0][1] + b * c[1][1] + d * c[2][1] + e * c[3][1],
  ];
}

function line(a: Pt, b: Pt): Cubic {
  return [
    a,
    [a[0] + (b[0] - a[0]) / 3, a[1] + (b[1] - a[1]) / 3],
    [a[0] + ((b[0] - a[0]) * 2) / 3, a[1] + ((b[1] - a[1]) * 2) / 3],
    b,
  ];
}

function sample(segs: readonly Cubic[], n = 14): Pt[] {
  const out: Pt[] = [];
  segs.forEach((c, i) => {
    for (let k = i === 0 ? 0 : 1; k <= n; k++) out.push(cubicAt(c, k / n));
  });
  return out;
}

/** y of a cubic (monotonic in x) at a given x. */
function yAtX(c: Cubic, x: number): number {
  let lo = 0;
  let hi = 1;
  const inc = c[3][0] > c[0][0];
  for (let i = 0; i < 28; i++) {
    const m = (lo + hi) / 2;
    const px = cubicAt(c, m)[0];
    if (px < x === inc) lo = m;
    else hi = m;
  }
  return cubicAt(c, (lo + hi) / 2)[1];
}

function tangentAt(pts: readonly Pt[], i: number): Pt {
  const a = pts[Math.max(0, i - 1)];
  const b = pts[Math.min(pts.length - 1, i + 1)];
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const l = Math.hypot(dx, dy) || 1;
  return [dx / l, dy / l];
}

/** An open polyline pushed `d` units toward `toward` (a point inside the shell). */
function offsetToward(pts: readonly Pt[], d: number, toward: Pt): Pt[] {
  return pts.map((p, i) => {
    const [tx, ty] = tangentAt(pts, i);
    let nx = -ty;
    let ny = tx;
    if (nx * (toward[0] - p[0]) + ny * (toward[1] - p[1]) < 0) {
      nx = -nx;
      ny = -ny;
    }
    return [p[0] + nx * d, p[1] + ny * d];
  });
}

/** A closed outline pushed `d` units OUTWARD (negative = inward). */
function offsetClosed(pts: readonly Pt[], d: number): Pt[] {
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    area += a[0] * b[1] - b[0] * a[1];
  }
  const sgn = area > 0 ? 1 : -1;
  const n = pts.length;
  return pts.map((p, i) => {
    const a = pts[(i - 1 + n) % n];
    const b = pts[(i + 1) % n];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const l = Math.hypot(dx, dy) || 1;
    return [p[0] + ((sgn * dy) / l) * d, p[1] + ((-sgn * dx) / l) * d];
  });
}

/** Both ends of an open polyline stretched along their tangents. */
function extend(pts: readonly Pt[], head: number, tail: number): Pt[] {
  const [ax, ay] = tangentAt(pts, 0);
  const [bx, by] = tangentAt(pts, pts.length - 1);
  const s = pts[0];
  const e = pts[pts.length - 1];
  return [[s[0] - ax * head, s[1] - ay * head], ...pts, [e[0] + bx * tail, e[1] + by * tail]];
}

/** The part of an open polyline on `keep`'s side of the line a→b, cut
 *  cleanly where it crosses (a straight, deliberate end). */
function trimByLine(pts: readonly Pt[], a: Pt, b: Pt, keep: Pt): Pt[] {
  const side = (p: Pt) => (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
  const k = Math.sign(side(keep)) || 1;
  const out: Pt[] = [];
  for (let i = 0; i < pts.length; i++) {
    const s = side(pts[i]) * k;
    if (i > 0) {
      const sp = side(pts[i - 1]) * k;
      if (sp < 0 !== s < 0) {
        const t = sp / (sp - s);
        const q = pts[i - 1];
        out.push([q[0] + (pts[i][0] - q[0]) * t, q[1] + (pts[i][1] - q[1]) * t]);
      }
    }
    if (s >= 0) out.push(pts[i]);
  }
  return out;
}

function polyD(pts: readonly Pt[], close = false): string {
  return (
    pts
      .map((p, i) => {
        const y = f1(p[1]);
        // a negative y needs no separator ("L12.3-4.5"); -0 prints as "0"
        return `${i === 0 ? 'M' : 'L'}${f1(p[0])}${y < 0 ? '' : ' '}${y}`;
      })
      .join('') + (close ? 'Z' : '')
  );
}

function cubicsD(segs: readonly Cubic[], close = false): string {
  let d = `M${segs[0][0][0]} ${segs[0][0][1]}`;
  for (const c of segs) d += `C${c[1][0]} ${c[1][1]} ${c[2][0]} ${c[2][1]} ${c[3][0]} ${c[3][1]}`;
  return d + (close ? 'Z' : '');
}

// ---- the helmet ------------------------------------------------------------------

/** The shell, clockwise from the back of the neck (side view, facing right). */
const SHELL_SEGS: readonly Cubic[] = [
  [
    [28, 88],
    [21, 87],
    [17.5, 80],
    [16.8, 70],
  ],
  [
    [16.8, 70],
    [15.6, 56],
    [16.5, 42],
    [22, 31],
  ],
  [
    [22, 31],
    [29, 17],
    [44, 10.5],
    [62, 9.6],
  ],
  [
    [62, 9.6],
    [81, 8.8],
    [98, 14],
    [107, 25],
  ],
  [
    [107, 25],
    [110.5, 29],
    [112.6, 32.5],
    [113.4, 36.5],
  ],
  [
    [113.4, 36.5],
    [115.4, 42],
    [116, 49.5],
    [115.2, 56.2],
  ],
  [
    [115.2, 56.2],
    [116.2, 62],
    [115.8, 68.5],
    [113, 73.2],
  ],
  [
    [113, 73.2],
    [110.8, 76.4],
    [107, 77.6],
    [102.5, 77.8],
  ],
  line([102.5, 77.8], [82, 79]),
  // the chin-bar skirt eases down into the neck roll (one long, soft curve,
  // not a notched ledge)
  [
    [82, 79],
    [68, 80.4],
    [58, 86],
    [48, 88],
  ],
  line([48, 88], [28, 88]),
];
const SHELL = cubicsD(SHELL_SEGS, true);

/** The crown contour, brow (front) → over the top → down the back of the
 *  head. It stops where the back meets the neck roll (SHELL_SEGS[0] curls
 *  forward into the rim, which would bend anything traced along it). */
const CROWN_SEGS: readonly Cubic[] = [4, 3, 2, 1].map((i) => {
  const c = SHELL_SEGS[i];
  return [c[3], c[2], c[1], c[0]] as Cubic;
});

/** The neck rim, chin → back of the neck. */
const RIM_SEGS: readonly Cubic[] = [SHELL_SEGS[7], SHELL_SEGS[8], SHELL_SEGS[9], SHELL_SEGS[10]];

/** The visor: a broad eye-port band that stays ~20 units tall to the front. */
const VISOR_TOP: Cubic = [
  [61, 34.6],
  [77, 31.6],
  [97, 32],
  [113.4, 36.5],
];
const VISOR_BOT: Cubic = [
  [115.2, 56.2],
  [99, 55.2],
  [81, 55.6],
  [64, 57.4],
];
const VISOR_SEGS: readonly Cubic[] = [
  VISOR_TOP,
  SHELL_SEGS[5],
  VISOR_BOT,
  [
    [64, 57.4],
    [57.5, 58],
    [54, 52],
    [54.6, 46],
  ],
  [
    [54.6, 46],
    [55.2, 40],
    [57.4, 35.6],
    [61, 34.6],
  ],
];
const VISOR = cubicsD(VISOR_SEGS, true);
const VISOR_PTS = sample(VISOR_SEGS, 12);

const PIVOT: Pt = [58.2, 46];
const SHELL_CENTER: Pt = [64, 52];

// ---- the soccer-ball panelling (MATCH BALL + the FIREBALL ball) --------------------

type V3 = [number, number, number];

const norm3 = (v: V3): V3 => {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
};
const lerp3 = (a: V3, b: V3, t: number): V3 => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];
const cross3 = (a: V3, b: V3): V3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const dot3 = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/** Rodrigues rotation of v about unit axis k by angle a. */
function rot3(v: V3, k: V3, a: number): V3 {
  const c = Math.cos(a);
  const s = Math.sin(a);
  const kv = cross3(k, v);
  const kd = dot3(k, v) * (1 - c);
  return [
    v[0] * c + kv[0] * s + k[0] * kd,
    v[1] * c + kv[1] * s + k[1] * kd,
    v[2] * c + kv[2] * s + k[2] * kd,
  ];
}

interface BallFaces {
  pentagons: V3[][];
  edges: [V3, V3][];
}

/** A truncated icosahedron (the classic 12-pentagon / 20-hexagon ball) on the
 *  unit sphere, built from an icosahedron: each vertex → a pentagon, each
 *  face → a hexagon, cut at the thirds of every edge. */
const BALL: BallFaces = (() => {
  const g = (1 + Math.sqrt(5)) / 2;
  const v: V3[] = [];
  for (const a of [-1, 1])
    for (const b of [-1, 1]) v.push([0, a, b * g], [a, b * g, 0], [b * g, 0, a]);
  const near = (a: V3, b: V3) =>
    Math.abs(Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) - 2) < 1e-6;
  const pentagons: V3[][] = v.map((c) => {
    const pts = v.filter((n) => near(c, n)).map((n) => lerp3(c, n, 1 / 3));
    const up = norm3(c);
    const ref = norm3(cross3(up, Math.abs(up[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0]));
    const ref2 = cross3(up, ref);
    return pts
      .map((p) => ({ p, a: Math.atan2(dot3(p, ref2), dot3(p, ref)) }))
      .sort((x, y) => x.a - y.a)
      .map((o) => o.p);
  });
  const edges: [V3, V3][] = [];
  for (const pent of pentagons)
    for (let i = 0; i < 5; i++) edges.push([pent[i], pent[(i + 1) % 5]]);
  for (let i = 0; i < v.length; i++) {
    for (let j = i + 1; j < v.length; j++) {
      if (near(v[i], v[j])) edges.push([lerp3(v[i], v[j], 1 / 3), lerp3(v[i], v[j], 2 / 3)]);
    }
  }
  return { pentagons, edges };
})();

/**
 * The ball projected (orthographic) with its first pentagon turned toward the
 * viewer direction `look`, rolled by `roll` radians: path data in a ±100 box
 * (scale with a transform). Edges are great-circle arcs cut into `steps`
 * pieces; anything behind the limb folds onto it, so panels hug the
 * silhouette. `keep` culls panels / seams with no point where they can show.
 * Runs once per module load (the geometry never changes, only the colours).
 */
function projectBall(
  look: V3,
  roll: number,
  steps: number,
  keep: (p: Pt) => boolean = () => true,
): { pent: string; seams: string } {
  const c0 = norm3(
    BALL.pentagons[0].reduce<V3>((s, p) => [s[0] + p[0], s[1] + p[1], s[2] + p[2]], [0, 0, 0]),
  );
  const target = norm3(look);
  const axis = cross3(c0, target);
  const al = Math.hypot(axis[0], axis[1], axis[2]);
  const ang = Math.acos(Math.max(-1, Math.min(1, dot3(c0, target))));
  const orient = (p: V3): V3 => {
    let q = norm3(p);
    if (al > 1e-9) q = rot3(q, [axis[0] / al, axis[1] / al, axis[2] / al], ang);
    return rot3(q, target, roll);
  };
  const flat = (q: V3): Pt => {
    if (q[2] >= 0) return [q[0] * 100, -q[1] * 100];
    const l = Math.hypot(q[0], q[1]) || 1;
    return [(q[0] / l) * 100, (-q[1] / l) * 100];
  };
  const arc = (a: V3, b: V3, n: number): V3[] => {
    const out: V3[] = [];
    for (let k = 0; k <= n; k++) out.push(orient(lerp3(a, b, k / n)));
    return out;
  };
  let pent = '';
  for (const poly of BALL.pentagons) {
    const ring: V3[] = [];
    for (let i = 0; i < 5; i++) ring.push(...arc(poly[i], poly[(i + 1) % 5], steps).slice(0, -1));
    if (ring.every((q) => q[2] < 0.02)) continue;
    const flatRing = ring.map(flat);
    if (!flatRing.some(keep)) continue;
    pent += polyD(flatRing, true);
  }
  let seams = '';
  for (const [a, b] of BALL.edges) {
    const pts = arc(a, b, steps);
    let run: Pt[] = [];
    for (let k = 0; k < pts.length; k++) {
      const q = pts[k];
      if (q[2] >= 0) {
        if (run.length === 0 && k > 0 && pts[k - 1][2] < 0) {
          const pq = pts[k - 1];
          const t = pq[2] / (pq[2] - q[2]);
          run.push(flat(lerp3(pq, q, t)));
        }
        run.push(flat(q));
      } else if (run.length) {
        const pq = pts[k - 1];
        const t = pq[2] / (pq[2] - q[2]);
        run.push(flat(lerp3(pq, q, t)));
        if (run.length > 1 && run.some(keep)) seams += polyD(run);
        run = [];
      }
    }
    if (run.length > 1 && run.some(keep)) seams += polyD(run);
  }
  return { pent, seams };
}

function inside(p: Pt, poly: readonly Pt[]): boolean {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

/** The shell panelling: a pentagon on the side of the head, tilted like a
 *  dome, mapped by translate(62 50) scale(.56 .5). Panels that land outside
 *  the shell or wholly under the visor are culled (they would never show). */
const SHELL_BALL = (() => {
  const shellPoly = offsetClosed(sample(SHELL_SEGS, 8), 2);
  const visorPoly = offsetClosed(VISOR_PTS, -1.5);
  return projectBall([-0.44, 0.2, 0.88], 0.2, 5, ([x, y]) => {
    const p: Pt = [62 + x * 0.56, 50 + y * 0.5];
    return inside(p, shellPoly) && !inside(p, visorPoly);
  });
})();
/** A face-on ball (the FIREBALL icon). */
const ICON_BALL = projectBall([0.06, 0.1, 1], 0.08, 3);

// ---- designs ---------------------------------------------------------------------

/** Where a design draws. `shell` is clipped to the shell; `mouth` is clipped to
 *  the visor (drawn over the glass); `back` sits behind the shell (horns,
 *  fins); `over` / `front` are unclipped on top (before / after the studio
 *  highlight). */
interface Layers {
  defs?: string;
  back?: string;
  shell?: string;
  mouth?: string;
  over?: string;
  front?: string;
  /** hide the stock rear spoiler (the dragon wears fins instead) */
  noSpoiler?: boolean;
  /** hide the helmet hardware: chin vents, visor lift tab, pivot screw */
  noHardware?: boolean;
  /** hide the visor glare (the dragon's mouth is not glass) */
  noGlare?: boolean;
  /** replace the smoked visor glass (the dragon's mouth) */
  glass?: string;
  /** the visor seal colour (the dragon's lip line) */
  seal?: string;
}

/** A stroke drawn twice: the contrast edge underneath, the colour on top. */
function edgedStroke(d: string, color: string, width: number, c: Paint, extra = ''): string {
  return (
    `<path d="${d}" fill="none" stroke="${c.edge}" stroke-opacity="${c.edgeOp}" stroke-width="${f1(width + 1.3)}"${extra}/>` +
    `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}"${extra}/>`
  );
}

/** A filled shape with the contrast edge. */
function edgedFill(d: string, fill: string, c: Paint, w = 1.1): string {
  return `<path d="${d}" fill="${fill}" stroke="${c.edge}" stroke-opacity="${c.edgeOp}" stroke-width="${w}" stroke-linejoin="round"/>`;
}

/** A band `d1`→`d2` units inside an open contour, its ends cut straight by
 *  the given lines (each keeps the side SHELL_CENTER is on). */
function bandD(path: readonly Pt[], d1: number, d2: number, cuts: readonly [Pt, Pt][]): string {
  const trim = (pts: Pt[]) =>
    cuts.reduce((acc, [a, b]) => trimByLine(acc, a, b, SHELL_CENTER), pts);
  const a = trim(offsetToward(path, d1, SHELL_CENTER));
  const b = trim(offsetToward(path, d2, SHELL_CENTER));
  return polyD([...a, ...b.reverse()], true);
}

// 0 CLASSIC: a bold stripe framing the visor, a fine pinstripe tracing it,
// and a neck band with its own pinstripe.
/** sits ~3 units of shell off the visor seal, so it never melts into the glass */
const CLASSIC_RING = polyD(offsetClosed(VISOR_PTS, 6.4), true);
/** the fine pinstripe starts at the visor's back end (by the pivot) and
 *  traces its top edge to the brow */
const CLASSIC_PIN = polyD(
  extend(offsetToward(sample([VISOR_SEGS[4], VISOR_TOP], 12), -12.6, [86, 46]), 0, 8),
);
const CLASSIC_NECK = 'M12 80.4C38 85.2 58 78.2 80 75.6C96 73.8 108 73 119 71.4';
const CLASSIC_NECK_PIN = 'M12 73.2C38 78 58 71 80 68.4C96 66.6 108 65.8 119 64.2';

function classic(c: Paint): Layers {
  return {
    shell:
      edgedStroke(CLASSIC_RING, c.s, 5, c, ' stroke-linejoin="round"') +
      edgedStroke(CLASSIC_PIN, c.s, 1.8, c, ' stroke-linecap="round" stroke-linejoin="round"') +
      edgedStroke(CLASSIC_NECK, c.s, 7, c) +
      edgedStroke(CLASSIC_NECK_PIN, c.s, 1.6, c),
  };
}

// 1 RACING STRIPES: two bold stripes over the crown, brow to the back of the
// head, a slim gap of shell between them and a band of shell outside them.
const STRIPE_PATH = extend(sample(CROWN_SEGS, 16), 0, 16);
/** front: a cut across the brow, slanted back, so both stripes stop clear of
 *  the visor; back: a cut that runs with the neck rim */
const STRIPE_CUTS: readonly [Pt, Pt][] = [
  [
    [109, 15],
    [92, 33],
  ],
  [
    [4, 70],
    [44, 81],
  ],
];
const STRIPE_A = bandD(STRIPE_PATH, 3.4, 9, STRIPE_CUTS);
const STRIPE_B = bandD(STRIPE_PATH, 11.6, 17.2, STRIPE_CUTS);

function racingStripes(c: Paint): Layers {
  return {
    shell: edgedFill(STRIPE_A, c.s, c, 1.3) + edgedFill(STRIPE_B, c.s, c, 1.3),
  };
}

// 2 MATCH BALL: the shell panelled like a classic ball.
function matchBall(c: Paint): Layers {
  const tf = 'transform="translate(62 50) scale(.56 .5)"';
  return {
    shell:
      `<g ${tf}>` +
      `<path d="${SHELL_BALL.seams}" fill="none" stroke="${c.seam}" stroke-width="3.6" stroke-linecap="round" stroke-linejoin="round" opacity=".9"/>` +
      `<path d="${SHELL_BALL.pent}" fill="${c.s}" stroke="${c.edge}" stroke-opacity="${c.edgeOp}" stroke-width="2.8" stroke-linejoin="round"/>` +
      `<path d="${SHELL_BALL.seams}" fill="none" stroke="${c.pLight}" stroke-width="1.1" stroke-dasharray="2.6 2.4" opacity=".55"/>` +
      '</g>',
  };
}

// 3 CHECKERED: a finish-flag band wrapping the lower shell, ending in a clean
// edge before the chin vents.
const CHECK_X0 = 14;
const CHECK_X1 = 100;
const CHECK = (() => {
  const cols = 11;
  const rows = 2;
  const h = 7.2;
  // the band dips a little toward the back, following the rim, and its
  // squares narrow where the shell turns away from the viewer
  const yb = (x: number) => 59.4 + 5.5 * ((CHECK_X1 - x) / (CHECK_X1 - CHECK_X0)) ** 1.4;
  const xs: number[] = [];
  for (let i = 0; i <= cols; i++) xs.push(CHECK_X0 + (CHECK_X1 - CHECK_X0) * (i / cols) ** 1.3);
  let on = '';
  let off = '';
  for (let i = 0; i < cols; i++) {
    for (let r = 0; r < rows; r++) {
      const a = xs[i];
      const b = xs[i + 1];
      const q: Pt[] = [
        [a, yb(a) + r * h],
        [b, yb(b) + r * h],
        [b, yb(b) + (r + 1) * h],
        [a, yb(a) + (r + 1) * h],
      ];
      // the front column is "on" on the top row, so the clean end reads
      if ((cols - 1 - i + r) % 2 === 0) on += polyD(q, true);
      else off += polyD(q, true);
    }
  }
  const rule = (dy: number) => {
    const pts: Pt[] = [];
    for (let x = 6; x < CHECK_X1; x += 4) pts.push([x, yb(Math.max(CHECK_X0, x)) + dy]);
    pts.push([CHECK_X1, yb(CHECK_X1) + dy]);
    return polyD(pts);
  };
  const top = yb(CHECK_X1) - 1.6;
  const bottom = yb(CHECK_X1) + rows * h + 1.6;
  return {
    on,
    off,
    top: rule(-1.6),
    bottom: rule(rows * h + 1.6),
    end: `M${CHECK_X1} ${f1(top)}L${CHECK_X1} ${f1(bottom)}`,
  };
})();

/** The flag's other colour: a true flag black or white, whichever stands
 *  clearest off the design colour without vanishing into the shell. */
const FLAG_INK = '#15171c';
const FLAG_WHITE = '#eef1f5';
function flagTone(c: Paint): string {
  const ls = lightness(c.s);
  const lp = lightness(c.p);
  const score = (hex: string) => {
    const l = lightness(hex);
    return Math.min(Math.abs(l - ls), Math.abs(l - lp) * 0.6);
  };
  return score(FLAG_WHITE) >= score(FLAG_INK) ? FLAG_WHITE : FLAG_INK;
}

function checkered(c: Paint): Layers {
  // on squares + rules wear the design colour; the off squares are a real
  // flag black or white, so even a same-colour pick reads as a finish flag
  return {
    shell:
      `<path d="${CHECK.off}" fill="${flagTone(c)}"/>` +
      `<path d="${CHECK.on}" fill="${c.s}" stroke="${c.edge}" stroke-opacity="${f1(c.edgeOp * 0.35)}" stroke-width=".6"/>` +
      edgedStroke(CHECK.top, c.s, 1.5, c) +
      edgedStroke(CHECK.bottom, c.s, 1.5, c) +
      edgedStroke(CHECK.end, c.s, 1.5, c, ' stroke-linecap="square"'),
  };
}

// 4 FIREBALL: a soccer ball in flames on the jaw, the fire streaming back.
/** The ball, between the visor and the skirt with shell all round it. */
const FB: Pt = [88.4, 67.6];
const FB_R = 10.6;
/** The fire was drawn around a ball at FL0; it rides along with the ball. */
const FL0: Pt = [91, 67];
const FL_SHIFT = `translate(${f1(FB[0] - FL0[0])} ${f1(FB[1] - FL0[1])})`;
/** The fire's silhouette: tongues streaming back from the ball, tips curling
 *  up like real flame. The two cores are the same shape scaled into the ball. */
const FLAME =
  'M92 56.6C82 56 74 58 68 58.4C61 58.8 55 57 50.5 50C51.5 55.6 54 59.4 58 61.6' +
  'C50 62.4 42 60.2 34.5 54.4C36.5 60.6 40 63.6 45 65C36 66.2 26 65.4 16.5 61.6' +
  'C24 68.6 34 70.4 44 70.6C36 73.2 28 76.6 21.5 80.6C33 80 43 77.6 51 75.4' +
  'C46 79.2 40 82.6 34 86.4C47 85 60 80.4 71 78.2C78 77.4 84 77.4 90 76.8L91 66.6Z';
/** small tongues licking over the ball's back edge */
const FLAME_LICKS =
  'M85.4 58.8C82.4 59.6 79 59.2 76 58C78.6 60.8 81 62.2 84 62.8Z' +
  'M83.2 65.2C80 65.8 76.4 65.4 73 64.4C76.2 67 79 68.4 82.6 69.2Z' +
  'M84.8 72.2C81.8 73.8 78.2 74.4 74.8 74C78 76.2 81.2 76.8 84.6 76.2Z';
const coreOf = (sx: number, sy: number) =>
  `transform="translate(${FL0[0]} ${FL0[1]}) scale(${sx} ${sy}) translate(${-FL0[0]} ${-FL0[1]})"`;

function fireball(c: Paint, id: string): Layers {
  // a grey / black / silver pick cannot colour a fire (it reads as smoke):
  // the body burns real fire and the pick outlines it instead
  const achro = chroma(c.s) < 0.18;
  const warm = burnsWarm(c.s);
  const core = achro ? '#ffc23a' : warm ? mix(c.s, '#ffb32e', 0.85) : mix(c.s, PALE, 0.55);
  const hot = warm ? '#fff6d0' : mix(c.s, PALE, 0.86);
  const k = FB_R / 100;
  const bodyStops = achro
    ? '<stop offset="0" stop-color="#ffc23a"/><stop offset=".38" stop-color="#ff7a1a"/><stop offset="1" stop-color="#b8260f"/>'
    : `<stop offset="0" stop-color="${c.sLight}"/><stop offset=".35" stop-color="${c.s}"/><stop offset="1" stop-color="${c.sShade}"/>`;
  const body = achro
    ? `<path d="${FLAME}" fill="url(#${id}fl)" stroke="${c.s}" stroke-width="1.5" stroke-linejoin="round"/>`
    : edgedFill(FLAME, `url(#${id}fl)`, c, 1.3);
  const licks = achro
    ? `<path d="${FLAME_LICKS}" fill="#ff8a1e" stroke="${c.s}" stroke-width=".9"/>`
    : `<path d="${FLAME_LICKS}" fill="${c.s}" stroke="${c.edge}" stroke-opacity="${c.edgeOp}" stroke-width=".8"/>`;
  return {
    defs:
      `<radialGradient id="${id}fb" cx=".36" cy=".3" r=".78">` +
      '<stop offset="0" stop-color="#ffffff"/><stop offset=".55" stop-color="#eef1f5"/>' +
      '<stop offset="1" stop-color="#aeb6c2"/></radialGradient>' +
      `<linearGradient id="${id}fl" x1="1" y1="0" x2="0" y2="0">${bodyStops}</linearGradient>` +
      `<clipPath id="${id}bc"><circle cx="${FB[0]}" cy="${FB[1]}" r="${FB_R}"/></clipPath>`,
    shell:
      `<g transform="${FL_SHIFT}">` +
      body +
      `<path d="${FLAME}" fill="${core}" ${coreOf(0.72, 0.66)}/>` +
      `<path d="${FLAME}" fill="${hot}" ${coreOf(0.46, 0.4)}/>` +
      '</g>',
    // the ball rides over the shading (a crisp white ball on any shell), still
    // clipped to the shell; on a near-white shell its dark ring is heavier
    over:
      `<g clip-path="url(#${id}c)">` +
      `<circle cx="${FB[0]}" cy="${FB[1]}" r="${f1(FB_R + (c.lightShell ? 1.5 : 1))}" fill="${INK}" opacity="${c.lightShell ? 0.88 : 0.6}"/>` +
      `<circle cx="${FB[0]}" cy="${FB[1]}" r="${FB_R}" fill="url(#${id}fb)"/>` +
      `<g clip-path="url(#${id}bc)"><g transform="translate(${FB[0]} ${FB[1]}) scale(${k})">` +
      `<path d="${ICON_BALL.seams}" fill="none" stroke="#5d6573" stroke-width="5" stroke-linecap="round"/>` +
      `<path d="${ICON_BALL.pent}" fill="#15171d"/>` +
      '</g></g>' +
      `<circle cx="${FB[0]}" cy="${FB[1]}" r="${FB_R}" fill="none" stroke="#1a1d24" stroke-width=".9"/>` +
      `<ellipse cx="${f1(FB[0] - 3.4)}" cy="${f1(FB[1] - 4.6)}" rx="3.4" ry="2" fill="#fff" opacity=".75" transform="rotate(-28 ${f1(FB[0] - 3.4)} ${f1(FB[1] - 4.6)})"/>` +
      `<g transform="${FL_SHIFT}">${licks}</g>` +
      '</g>',
  };
}

// 5 DRAGON: the helmet IS the head; the visor is the open mouth.
const DRAGON_FANGS = (() => {
  const top: [number, number, number][] = [
    [108.6, 1.9, 6.2],
    [101.5, 3, 11.5],
    [93.6, 1.7, 5],
    [86.6, 1.8, 5.6],
    [78.2, 2.8, 10],
    [70.6, 1.6, 4.6],
  ];
  const bot: [number, number, number][] = [
    [105.2, 2.4, 8],
    [97.4, 1.6, 4.4],
    [90, 2.6, 9],
    [82.2, 1.6, 4.4],
    [73.6, 2.2, 7],
  ];
  const fang = (x: number, w: number, len: number, down: boolean) => {
    const edge = (xx: number) => yAtX(down ? VISOR_TOP : VISOR_BOT, xx);
    const y0 = edge(x - w) + (down ? -1.5 : 1.5);
    const y1 = edge(x + w) + (down ? -1.5 : 1.5);
    const tipX = x - w * 0.55;
    const tipY = edge(x) + (down ? len : -len);
    const mid = (y0 + tipY) / 2;
    return (
      `M${f1(x - w)} ${f1(y0)}C${f1(x - w)} ${f1(mid)} ${f1(tipX - w * 0.15)} ${f1(tipY + (down ? -len * 0.25 : len * 0.25))} ${f1(tipX)} ${f1(tipY)}` +
      `C${f1(tipX + w * 0.5)} ${f1(tipY + (down ? -len * 0.3 : len * 0.3))} ${f1(x + w)} ${f1(mid)} ${f1(x + w)} ${f1(y1)}Z`
    );
  };
  return (
    top.map(([x, w, l]) => fang(x, w, l, true)).join('') +
    bot.map(([x, w, l]) => fang(x, w, l, false)).join('')
  );
})();

/**
 * A tapered, curved horn along a cubic centreline: `w0` wide at the root,
 * a point at the tip, with ridge rings across it (bulged toward the tip).
 */
function hornShape(
  c: Cubic,
  w0: number,
  rings: readonly number[],
): { body: string; rings: string } {
  const n = 26;
  const left: Pt[] = [];
  const right: Pt[] = [];
  const at = (t: number) => {
    const p = cubicAt(c, t);
    const q = cubicAt(c, Math.min(1, t + 0.01));
    const r = cubicAt(c, Math.max(0, t - 0.01));
    const dx = q[0] - r[0];
    const dy = q[1] - r[1];
    const l = Math.hypot(dx, dy) || 1;
    const w = (w0 / 2) * (1 - t) ** 0.8;
    return { p, nx: -dy / l, ny: dx / l, tx: dx / l, ty: dy / l, w };
  };
  for (let k = 0; k <= n; k++) {
    const { p, nx, ny, w } = at(k / n);
    left.push([p[0] + nx * w, p[1] + ny * w]);
    right.push([p[0] - nx * w, p[1] - ny * w]);
  }
  const ring = rings
    .map((t) => {
      const { p, nx, ny, tx, ty, w } = at(t);
      const a: Pt = [p[0] + nx * w * 0.92, p[1] + ny * w * 0.92];
      const b: Pt = [p[0] - nx * w * 0.92, p[1] - ny * w * 0.92];
      const m: Pt = [p[0] + tx * w * 0.7, p[1] + ty * w * 0.7];
      return `M${f1(a[0])} ${f1(a[1])}Q${f1(m[0])} ${f1(m[1])} ${f1(b[0])} ${f1(b[1])}`;
    })
    .join('');
  return { body: polyD([...left, ...right.reverse()], true), rings: ring };
}

/** near horn, then the far horn (a touch smaller, set further forward):
 *  rooted behind the crown, sweeping back, the tips turning up */
const HORN_NEAR = hornShape(
  [
    [47, 15.4],
    [35, 4.6],
    [18, 12.6],
    [3.4, 4.2],
  ],
  11.6,
  [0.22, 0.38, 0.54, 0.7],
);
const HORN_FAR = hornShape(
  [
    [57.5, 13.6],
    [50, 3.8],
    [40, 3.2],
    [28.5, 2.2],
  ],
  9.2,
  [0.34, 0.56],
);

/** neck spines, back of the head, top → bottom, graduated (the biggest on
 *  top): [root top, root bottom, tip] */
const DRAGON_SPINES: readonly [Pt, Pt, Pt][] = [
  [
    [24.6, 28.6],
    [19.6, 41],
    [6.6, 25.4],
  ],
  [
    [18.6, 44.2],
    [16.9, 53.6],
    [6.2, 43.4],
  ],
  [
    [16.4, 56.6],
    [16.3, 64.6],
    [8.8, 56.8],
  ],
  [
    [16.6, 67.4],
    [17.4, 74.6],
    [11.6, 68.4],
  ],
];
const DRAGON_SPINE_D = DRAGON_SPINES.map(
  ([a, b, t]) =>
    `M${a[0]} ${a[1]}Q${f1((a[0] + t[0]) / 2 + 1)} ${f1((a[1] + t[1]) / 2 - 2.2)} ${t[0]} ${t[1]}` +
    `Q${f1((b[0] + t[0]) / 2 + 2.4)} ${f1((b[1] + t[1]) / 2 - 0.4)} ${b[0]} ${b[1]}Z`,
).join('');
/** the webbed frill strung between the spine tips */
const DRAGON_WEB = (() => {
  const t = DRAGON_SPINES.map((s) => s[2]);
  return (
    `M24 30L${t[0][0]} ${t[0][1]}Q13.2 37 ${t[1][0]} ${t[1][1]}Q12.6 50.4 ${t[2][0]} ${t[2][1]}` +
    `Q13.2 62.6 ${t[3][0]} ${t[3][1]}Q14.6 72 17.6 76.4L26 76.4Z`
  );
})();
/** the crescent jaw plate: a band that swells from nothing to a backward
 *  point and back, following the mouth's back end outside the lip ring */
const DRAGON_JAW = (() => {
  const path = sample([VISOR_SEGS[3], VISOR_SEGS[4]], 14);
  const inner = offsetToward(path, -4.2, [86, 46]);
  const outer = path.map((p, i) => {
    const u = i / (path.length - 1);
    const w =
      4.2 + 4.2 * Math.sin(Math.PI * u) ** 0.8 + 4.4 * Math.max(0, 1 - Math.abs(u - 0.5) * 6);
    return offsetToward(
      [path[Math.max(0, i - 1)], p, path[Math.min(path.length - 1, i + 1)]],
      -w,
      [86, 46],
    )[1];
  });
  return polyD([...inner, ...outer.reverse()], true);
})();
const DRAGON_JAW_RIDGE = polyD(
  offsetToward(sample([VISOR_SEGS[3], VISOR_SEGS[4]], 14), -6.6, [86, 46]).slice(4, -4),
);
const BONE = '#efe3bf';

function dragon(c: Paint, id: string): Layers {
  // Horns, spines and frill wear the secondary, blended toward bone so a dark
  // pick still reads as horn (not a grey scratch) on the dark page.
  const darkS = lightness(c.s) < 30;
  const hornBase = darkS ? mix(c.s, BONE, 0.5) : c.s;
  const hornTip = mix(c.s, BONE, darkS ? 0.9 : 0.6);
  const hornMid = mix(hornBase, hornTip, 0.5);
  const hornRim = lightness(hornMid) < 45 ? mix(hornBase, PALE, 0.6) : deepen(hornBase, 0.62);
  const hornRidge = deepen(hornBase, 0.4);
  const web = deepen(hornBase, 0.32);
  const brow = mix(c.s, INK, 0.2);
  const browRim = lightness(brow) < 40 ? mix(brow, PALE, 0.5) : deepen(brow, 0.6);
  const hornFill = (g: string) =>
    `<linearGradient id="${id}${g}" x1="1" y1="1" x2="0" y2="0">` +
    `<stop offset="0" stop-color="${hornBase}"/><stop offset=".55" stop-color="${hornMid}"/>` +
    `<stop offset="1" stop-color="${hornTip}"/></linearGradient>`;
  return {
    noSpoiler: true,
    noHardware: true,
    noGlare: true,
    seal: c.sShade,
    defs:
      `<radialGradient id="${id}ember" cx=".18" cy=".52" r=".95">` +
      '<stop offset="0" stop-color="#ffe08a"/><stop offset=".18" stop-color="#ff9b2a"/>' +
      '<stop offset=".45" stop-color="#e2380f"/><stop offset=".78" stop-color="#5c0b05"/>' +
      '<stop offset="1" stop-color="#1b0503"/></radialGradient>' +
      `<linearGradient id="${id}ivory" x1="0" y1="0" x2="1" y2="1">` +
      '<stop offset="0" stop-color="#fffdf2"/><stop offset="1" stop-color="#d9c99a"/></linearGradient>' +
      `<radialGradient id="${id}eye" cx=".45" cy=".5" r=".6">` +
      '<stop offset="0" stop-color="#fffbd0"/><stop offset=".45" stop-color="#ffd23a"/>' +
      '<stop offset="1" stop-color="#ff6a00"/></radialGradient>' +
      `<radialGradient id="${id}halo" cx=".5" cy=".5" r=".5">` +
      '<stop offset="0" stop-color="#ffb02a" stop-opacity=".8"/><stop offset="1" stop-color="#ff6a00" stop-opacity="0"/></radialGradient>' +
      hornFill('horn') +
      `<linearGradient id="${id}hs" x1="1" y1="0" x2="0" y2="0">` +
      `<stop offset="0" stop-color="${hornBase}"/><stop offset="1" stop-color="${hornTip}"/></linearGradient>` +
      // the scales: big, soft cells that fade out toward the back of the neck
      `<pattern id="${id}scale" width="12" height="10" patternUnits="userSpaceOnUse">` +
      `<path d="M0 10A6 6 0 0 1 12 10M-6 5A6 6 0 0 1 6 5M6 5A6 6 0 0 1 18 5" fill="none" stroke="${c.s}" stroke-width="1.2"/>` +
      '</pattern>' +
      `<linearGradient id="${id}sf" x1="0" y1="0" x2="1" y2="0">` +
      '<stop offset=".1" stop-color="#1a1a1a"/><stop offset=".7" stop-color="#fff"/></linearGradient>' +
      `<mask id="${id}sm" maskUnits="userSpaceOnUse" x="0" y="0" width="120" height="100">` +
      `<rect width="120" height="100" fill="url(#${id}sf)"/></mask>`,
    back:
      // the frill, the spines on it, then the far horn and the near horn (all
      // stand against the page, so they wear a page-facing rim)
      `<path d="${DRAGON_WEB}" fill="${web}" stroke="${hornRim}" stroke-width=".9" stroke-linejoin="round" opacity=".92"/>` +
      `<path d="${DRAGON_SPINE_D}" fill="url(#${id}hs)" stroke="${hornRim}" stroke-width="1.4" stroke-linejoin="round"/>` +
      `<path d="${HORN_FAR.body}" fill="${deepen(hornMid, 0.22)}" stroke="${hornRim}" stroke-width="1.4" stroke-linejoin="round"/>` +
      `<path d="${HORN_FAR.rings}" fill="none" stroke="${hornRidge}" stroke-width="1" stroke-linecap="round"/>` +
      `<path d="${HORN_NEAR.body}" fill="url(#${id}horn)" stroke="${hornRim}" stroke-width="1.6" stroke-linejoin="round"/>` +
      `<path d="${HORN_NEAR.rings}" fill="none" stroke="${hornRidge}" stroke-width="1.1" stroke-linecap="round"/>`,
    shell:
      // scales, a lip ring around the mouth
      `<path d="${SHELL}" fill="url(#${id}scale)" mask="url(#${id}sm)" opacity=".26"/>` +
      `<path d="${VISOR}" fill="none" stroke="${c.sShade}" stroke-width="5.2" stroke-linejoin="round"/>` +
      `<path d="${VISOR}" fill="none" stroke="${c.s}" stroke-width="2.6" stroke-linejoin="round"/>`,
    glass: `<path d="${VISOR}" fill="url(#${id}ember)"/>`,
    mouth:
      `<path d="${VISOR}" fill="#05070b" opacity=".18"/>` +
      `<path d="${DRAGON_FANGS}" fill="url(#${id}ivory)" stroke="#4a3418" stroke-width=".6" stroke-linejoin="round"/>`,
    over:
      // a crescent jaw plate hugging the back of the mouth, where a helmet
      // would carry its visor screw
      `<path d="${DRAGON_JAW}" fill="${c.s}" stroke="${c.edge}" stroke-opacity="${c.edgeOp}" stroke-width="1.1" stroke-linejoin="round"/>` +
      `<path d="${DRAGON_JAW_RIDGE}" fill="none" stroke="${c.sLight}" stroke-width=".9" stroke-linecap="round" opacity=".7"/>` +
      // jaw-scale ridges on the chin (where the helmet had its vents)
      `<path d="M100.6 64.4C104 62.9 107.6 62.7 111 63.5M100.2 68.6C103.7 67.2 107 67 110.2 67.8M100.8 72.6C103.7 71.4 106.1 71.2 108.4 71.8" fill="none" stroke="${c.pShade}" stroke-width="1.7" stroke-linecap="round" opacity=".75"/>` +
      // snout ridge with scutes + the nostril over the mouth's front
      `<path d="M82 17.6C95 18.6 106 23.4 113.4 29.4C116.6 32 118.4 34 117.8 35.6C117.2 37 115.6 37.2 113.4 36.5C107 33.4 99 31.8 90 31.4C86 27.4 83.6 22.6 82 17.6Z" fill="${c.p}" stroke="${c.pDeep}" stroke-width="1"/>` +
      `<path d="M84 19.6C96 21 106 25.6 113 31.2" fill="none" stroke="${c.pLight}" stroke-width="1.4" stroke-linecap="round" opacity=".8"/>` +
      `<path d="M88 19.2L91 15.8L93 20.4ZM96 21.4L99.4 18.2L101 23ZM103.6 24.8L107.2 22.2L108.2 27.2Z" fill="${c.s}" stroke="${c.edge}" stroke-opacity="${c.edgeOp}" stroke-width=".8" stroke-linejoin="round"/>` +
      '<path d="M111.6 30.6C113.4 30.2 115.4 31.4 115.4 33C113.6 33.6 111.6 32.6 111.6 30.6Z" fill="#14070a"/>' +
      '<path d="M112.4 31.3C113.6 31.2 114.6 31.9 114.7 32.7" fill="none" stroke="#ff7a1c" stroke-width=".7" opacity=".8"/>' +
      // the glowing eye (big enough to read on a chip) under a heavy brow
      `<ellipse cx="68.2" cy="26" rx="14.5" ry="8.8" fill="url(#${id}halo)"/>` +
      `<path d="M59.4 28.2C62.4 22 71 20 77.2 23.2C75.8 28.8 66.6 31.8 59.4 28.2Z" fill="url(#${id}eye)" stroke="#2a0e05" stroke-width=".9"/>` +
      '<ellipse cx="68.8" cy="25.9" rx="1.35" ry="3.8" fill="#120604" transform="rotate(14 68.8 25.9)"/>' +
      '<circle cx="66" cy="24.6" r=".9" fill="#fff" opacity=".85"/>' +
      `<path d="M52.6 26.8C56 16.6 67.6 12.4 82 16.4C85.4 17.4 85.8 19.6 82.8 20.4C76 20.6 70.4 21.2 66.4 22.4C61.8 23.2 57.2 24.6 52.6 26.8Z" fill="${brow}" stroke="${browRim}" stroke-width="1.1" stroke-linejoin="round"/>` +
      `<path d="M57 22.8C61.4 18.6 69 17 76.6 18.2" fill="none" stroke="${c.sLight}" stroke-width="1.1" stroke-linecap="round" opacity=".75"/>`,
  };
}

// 6 LIGHTNING: a bold bolt down the side behind the visor, and a slim echo
// bolt in the open shell below the visor's back end; both stay clear of the
// glass and the pivot so the zig-zag always shows.
const BOLT_MAIN = 'M44 6L26 43L37.5 43L23.5 85L51 35L41.5 35L58 6Z';
const BOLT_ECHO = 'M59.6 60.6L50.6 73.4L55 73.4L46.8 88.6L64.6 69.6L60 69.6L66 60.6Z';

function lightning(c: Paint): Layers {
  return {
    shell: edgedFill(BOLT_MAIN, c.s, c, 1.3) + edgedFill(BOLT_ECHO, c.s, c, 1),
  };
}

function designLayers(design: number, c: Paint, id: string): Layers {
  switch (design) {
    case 1:
      return racingStripes(c);
    case 2:
      return matchBall(c);
    case 3:
      return checkered(c);
    case 4:
      return fireball(c, id);
    case 5:
      return dragon(c, id);
    case 6:
      return lightning(c);
    default:
      return classic(c);
  }
}

function clampDesign(v: unknown): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : 0;
  return Math.min(HELMET_DESIGNS - 1, Math.max(0, n));
}

/**
 * One helmet as an SVG string. Out-of-range indices clamp (a stale cache can
 * never draw nothing) and nothing here throws.
 */
export function helmetSvg(helmet: Helmet, opts: { title?: string } = {}): string {
  const id = `hm${++seq}`;
  const c = paintFor(helmet);
  const L = designLayers(clampDesign(helmet.design), c, id);
  const title = opts.title ? `<title>${escapeXml(opts.title)}</title>` : '';
  const hwColor = c.darkShell ? '#7b8594' : '#05070b';
  const sealColor = L.seal ?? hwColor;
  return (
    `<svg viewBox="0 0 120 100" xmlns="http://www.w3.org/2000/svg" role="img"${opts.title ? '' : ' aria-hidden="true"'}>` +
    title +
    '<defs>' +
    `<linearGradient id="${id}s" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0" stop-color="${c.pLight}"/><stop offset=".34" stop-color="${c.p}"/>` +
    `<stop offset=".66" stop-color="${c.p}"/><stop offset="1" stop-color="${c.pShade}"/></linearGradient>` +
    `<linearGradient id="${id}v" x1="0" y1="0" x2="1" y2="1">` +
    '<stop offset="0" stop-color="#2b3443"/><stop offset=".55" stop-color="#0b0e14"/>' +
    '<stop offset="1" stop-color="#05070b"/></linearGradient>' +
    `<linearGradient id="${id}r" x1="0" y1="0" x2="0" y2="1">` +
    '<stop offset="0" stop-color="#fff" stop-opacity=".5"/><stop offset=".45" stop-color="#fff" stop-opacity="0"/></linearGradient>' +
    `<radialGradient id="${id}h" cx=".36" cy=".2" r=".55">` +
    '<stop offset="0" stop-color="#fff" stop-opacity=".55"/>' +
    '<stop offset=".45" stop-color="#fff" stop-opacity=".1"/>' +
    '<stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient>' +
    `<clipPath id="${id}c"><path d="${SHELL}"/></clipPath>` +
    `<clipPath id="${id}vc"><path d="${VISOR}"/></clipPath>` +
    (L.defs ?? '') +
    '</defs>' +
    // soft contact shadow
    '<ellipse cx="64" cy="92.5" rx="42" ry="4" fill="#000" opacity=".35"/>' +
    // rear spoiler lip (behind the shell; only the lip shows)
    (L.noSpoiler
      ? ''
      : `<path d="M30 24C24 25 17 27.6 12.6 31C11.6 31.8 12 33.4 13.4 33.4C17.6 33.2 22.4 33.8 27 36Z" fill="${c.pShade}" stroke="${c.pDeep}" stroke-width="1"/>` +
        `<path d="M13.4 31.2C17.6 28.8 22 27.4 27 26.6" fill="none" stroke="${c.pLight}" stroke-width=".9" stroke-linecap="round" opacity=".7"/>`) +
    (L.back ?? '') +
    // shell + design (clipped) + shading
    `<path d="${SHELL}" fill="url(#${id}s)"/>` +
    `<g clip-path="url(#${id}c)">` +
    (L.shell ?? '') +
    // the lower-shell shade drawn OVER the design so it wraps the volume
    '<path d="M0 70C30 77 80 76 120 64L120 100L0 100Z" fill="#000" opacity=".2"/>' +
    // a cool rim light along the crown (keeps dark shells off a dark page)
    `<path d="${SHELL}" fill="none" stroke="url(#${id}r)" stroke-width="3.2"/>` +
    // neck lining
    `<path d="${cubicsD(RIM_SEGS)}" fill="none" stroke="#0b0c10" stroke-width="3.4" opacity=".85"/>` +
    '</g>' +
    `<path d="${SHELL}" fill="none" stroke="${c.pDeep}" stroke-width="1.5" stroke-linejoin="round"/>` +
    // chin vents
    (L.noHardware
      ? ''
      : `<path d="M101.5 63.4L110.6 62.6M101 67.6L109.6 66.9M101.4 71.6L107.6 71.1" stroke="${c.darkShell ? '#000' : '#05070b'}" stroke-width="1.9" stroke-linecap="round" opacity=".62"/>`) +
    // visor: glass (or the dragon's mouth) + reflections + seal
    (L.glass ?? `<path d="${VISOR}" fill="url(#${id}v)"/>`) +
    `<g clip-path="url(#${id}vc)">` +
    (L.mouth ?? '') +
    (L.noGlare
      ? ''
      : '<path d="M60 58L74 33L83 33L69 58Z" fill="#fff" opacity=".06"/>' +
        '<path d="M66 39C80 35.4 99 36 110 40.6" fill="none" stroke="#ffe7a3" stroke-width="2" stroke-linecap="round" opacity=".72"/>' +
        '<path d="M68 51.6C80 50.2 96 50 108 51" fill="none" stroke="#9fd4ff" stroke-width="1.1" stroke-linecap="round" opacity=".35"/>') +
    '</g>' +
    `<path d="${VISOR}" fill="none" stroke="${sealColor}" stroke-width="1.5" stroke-linejoin="round"/>` +
    // visor lift tab (lower front) + pivot plate and screw (back end)
    (L.noHardware
      ? ''
      : `<path d="M104 56.6L111.4 57L111 59.6C108.6 60 106 59.8 104.2 59Z" fill="${c.pDeep}" stroke="${hwColor}" stroke-width=".6"/>` +
        `<circle cx="${PIVOT[0]}" cy="${PIVOT[1]}" r="5.6" fill="${c.pShade}" stroke="${hwColor}" stroke-width="1"/>` +
        `<circle cx="${PIVOT[0]}" cy="${PIVOT[1]}" r="3.6" fill="${c.pDeep}"/>` +
        `<circle cx="${PIVOT[0]}" cy="${PIVOT[1]}" r="2.4" fill="#c8ced8"/>` +
        `<path d="M${PIVOT[0] - 1.5} ${PIVOT[1] + 0.9}L${PIVOT[0] + 1.5} ${PIVOT[1] - 0.9}" stroke="#3a414c" stroke-width=".8" stroke-linecap="round"/>`) +
    (L.over ?? '') +
    // studio highlight
    `<path d="${SHELL}" fill="url(#${id}h)"/>` +
    (L.front ?? '') +
    '</svg>'
  );
}

function escapeXml(s: string): string {
  return s.replace(/[<>&"']/g, (c) =>
    c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '&' ? '&amp;' : c === '"' ? '&quot;' : '&#39;',
  );
}

/** A small helmet chip element (roster rows, friend rows, invites). */
export function helmetEl(
  helmet: Helmet,
  className = 'crew-helmet',
  title?: string,
): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = className;
  span.innerHTML = helmetSvg(helmet, { title });
  return span;
}

/** 'DRAGON · RACING RED & BLACK' (or 'DRAGON · RACING RED' for one colour). */
export function helmetLabel(h: Helmet): string {
  const design = HELMET_DESIGN_NAMES[clampDesign(h.design)];
  const p = CLUB_COLORS[paletteIndex(h.primary)].name;
  const s = CLUB_COLORS[paletteIndex(h.secondary)].name;
  return p === s ? `${design} · ${p}` : `${design} · ${p} & ${s}`;
}

/** The CAPTAIN badge — a football captain's armband with the "C" (the
 *  soccer-true mark for the crew's captain). A function, not a constant: every
 *  copy needs its own gradient id (ids are document-global, and a gradient
 *  defined inside a hidden element stops painting for the others). */
export function captainBadgeSvg(): string {
  const id = `cap${++seq}`;
  return (
    '<svg viewBox="0 0 40 24" aria-hidden="true">' +
    `<defs><linearGradient id="${id}" x1="0" y1="0" x2="0" y2="1">` +
    '<stop offset="0" stop-color="#fff1b8"/><stop offset=".5" stop-color="#f3bd3a"/>' +
    '<stop offset="1" stop-color="#a86f12"/></linearGradient></defs>' +
    `<rect x="1" y="3" width="38" height="18" rx="5" fill="url(#${id})"/>` +
    '<rect x="1" y="3" width="38" height="18" rx="5" fill="none" stroke="#7a4f08" stroke-width="1.2"/>' +
    '<rect x="4" y="5.5" width="3" height="13" rx="1.2" fill="#7a4f08" opacity=".35"/>' +
    '<rect x="33" y="5.5" width="3" height="13" rx="1.2" fill="#7a4f08" opacity=".35"/>' +
    '<path d="M25.4 9.2a6 6 0 1 0 0 5.6" fill="none" stroke="#14161c" stroke-width="3.2" stroke-linecap="round"/>' +
    '</svg>'
  );
}
